import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, RefreshCw, Search, ArrowLeft, X, Save, Stethoscope } from 'lucide-react';

const scoreClass = (score) => {
  if (score == null) return 'text-slate-400';
  if (score >= 80) return 'text-emerald-600';
  if (score >= 65) return 'text-amber-600';
  if (score >= 45) return 'text-orange-600';
  return 'text-rose-600';
};

const AdminHealthAnalysesPage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/health-analyses`, { credentials: 'include' });
      if (res.status === 401) {
        navigate('/admin');
        return;
      }
      if (!res.ok) throw new Error('Fetch failed');
      const data = await res.json();
      setRecords(data);
    } catch {
      setError('Failed to load health analyses.');
    } finally {
      setLoading(false);
    }
  }, [API_URL, navigate]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const filtered = records.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.full_name || '').toLowerCase().includes(q) ||
           (r.email || '').toLowerCase().includes(q) ||
           (r.phone || '').toLowerCase().includes(q);
  });

  const openRecord = (r) => {
    setSelected(r);
    setNotes(r.doctor_notes || '');
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/health-analyses/${selected.id}/notes?notes=${encodeURIComponent(notes)}`,
        { method: 'PATCH', credentials: 'include' }
      );
      if (res.ok) {
        setSelected({ ...selected, doctor_notes: notes });
        setRecords((prev) => prev.map((r) => r.id === selected.id ? { ...r, doctor_notes: notes } : r));
      }
    } finally {
      setSavingNotes(false);
    }
  };

  const handleLogout = async () => {
    try { await fetch(`${API_URL}/api/admin/logout`, { method: 'POST', credentials: 'include' }); } catch { /* noop */ }
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="p-2 text-[#64748B] hover:text-[#1E88E5] hover:bg-[#F1F5F9] rounded-lg" data-testid="back-to-consultations">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-[#1F2933]">Health Analyses</h1>
              <p className="text-sm text-[#64748B]">Tcube Full Body Health Analysis · {records.length} record{records.length === 1 ? '' : 's'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchRecords} disabled={loading}
              className="p-2 text-[#64748B] hover:text-[#1E88E5] hover:bg-[#F1F5F9] rounded-lg transition-colors" data-testid="ha-refresh">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-[#64748B] hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid="ha-logout">
              <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-4 mb-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
            <input
              type="text"
              placeholder="Search by name, email or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-[#CBD5E1] rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none"
              data-testid="ha-search"
            />
          </div>
        </div>

        {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

        <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden">
          {loading ? (
            <p className="p-12 text-center text-[#64748B]">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="p-12 text-center text-[#64748B]">No health analyses yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                  <tr className="text-left text-[#64748B] text-xs uppercase tracking-wider">
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Age</th>
                    <th className="px-4 py-3">Overall</th>
                    <th className="px-4 py-3">Metabolic</th>
                    <th className="px-4 py-3">Cardio</th>
                    <th className="px-4 py-3">Completeness</th>
                    <th className="px-4 py-3">Submitted</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`ha-row-${r.id}`}>
                      <td className="px-4 py-3 font-medium text-[#1F2933]">{r.full_name || <span className="text-[#94A3B8] italic">Anonymous</span>}</td>
                      <td className="px-4 py-3 text-[#64748B]">{r.email || '-'}</td>
                      <td className="px-4 py-3 text-[#64748B]">{r.age || '-'}</td>
                      <td className={`px-4 py-3 font-bold ${scoreClass(r.scores?.overall)}`}>{r.scores?.overall ?? '-'}</td>
                      <td className={`px-4 py-3 ${scoreClass(r.scores?.metabolic)}`}>{r.scores?.metabolic ?? '-'}</td>
                      <td className={`px-4 py-3 ${scoreClass(r.scores?.cardiovascular)}`}>{r.scores?.cardiovascular ?? '-'}</td>
                      <td className="px-4 py-3 text-[#64748B]">{r.completeness != null ? `${r.completeness}%` : '-'}</td>
                      <td className="px-4 py-3 text-[#64748B] text-xs">{r.created_at ? new Date(r.created_at).toLocaleString() : '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => openRecord(r)}
                          className="px-3 py-1.5 text-xs font-semibold text-white bg-[#1E88E5] hover:bg-[#1976D2] rounded-lg" data-testid={`ha-view-${r.id}`}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-30 bg-black/50 flex justify-end" onClick={() => setSelected(null)} data-testid="ha-detail-drawer">
          <div className="w-full max-w-2xl bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-[#1E88E5]" />
                <h2 className="font-bold text-[#1F2933]">{selected.full_name || 'Anonymous'}</h2>
              </div>
              <button onClick={() => setSelected(null)} className="p-1.5 hover:bg-[#F1F5F9] rounded-lg text-[#64748B]" data-testid="ha-close-drawer">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Scores grid */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  ['Overall', selected.scores?.overall],
                  ['Metabolic', selected.scores?.metabolic],
                  ['Cardiovascular', selected.scores?.cardiovascular],
                  ['Diabetes', selected.scores?.diabetes],
                  ['Obesity', selected.scores?.obesity],
                  ['Fitness', selected.scores?.fitness]
                ].map(([k, v]) => (
                  <div key={k} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 text-center">
                    <p className="text-[#64748B] text-xs mb-1">{k}</p>
                    <p className={`text-xl font-bold ${scoreClass(v)}`}>{v ?? '-'}</p>
                  </div>
                ))}
              </div>

              {/* Patient info */}
              <div>
                <h3 className="text-sm font-bold text-[#1F2933] mb-2 uppercase tracking-wider">Patient Details</h3>
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {[
                    ['Email', selected.email], ['Phone', selected.phone],
                    ['Age', selected.age], ['Gender', selected.gender],
                    ['Height', selected.height_cm ? `${selected.height_cm} cm` : null],
                    ['Weight', selected.weight_kg ? `${selected.weight_kg} kg` : null],
                    ['Waist', selected.waist_cm ? `${selected.waist_cm} cm` : null],
                    ['Hip', selected.hip_cm ? `${selected.hip_cm} cm` : null],
                    ['Neck', selected.neck_cm ? `${selected.neck_cm} cm` : null],
                    ['Activity', selected.activity_level]
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-[#E2E8F0] py-1">
                      <span className="text-[#64748B]">{k}</span>
                      <span className="text-[#1F2933] font-medium">{v || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Health markers */}
              <div>
                <h3 className="text-sm font-bold text-[#1F2933] mb-2 uppercase tracking-wider">Health Markers</h3>
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {[
                    ['Blood Pressure', selected.sbp && selected.dbp ? `${selected.sbp}/${selected.dbp}` : null],
                    ['HbA1c', selected.hba1c ? `${selected.hba1c}%` : null],
                    ['Fasting Glucose', selected.fbs ? `${selected.fbs} mg/dL` : null],
                    ['Postprandial', selected.ppbs ? `${selected.ppbs} mg/dL` : null],
                    ['Fasting Insulin', selected.fasting_insulin ? `${selected.fasting_insulin} µIU/mL` : null],
                    ['Total Cholesterol', selected.total_cholesterol ? `${selected.total_cholesterol} mg/dL` : null],
                    ['LDL', selected.ldl ? `${selected.ldl} mg/dL` : null],
                    ['HDL', selected.hdl ? `${selected.hdl} mg/dL` : null],
                    ['Triglycerides', selected.triglycerides ? `${selected.triglycerides} mg/dL` : null],
                    ['Resting HR', selected.resting_hr ? `${selected.resting_hr} bpm` : null],
                    ['Body Fat %', selected.body_fat_pct ? `${selected.body_fat_pct}%` : null]
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-[#E2E8F0] py-1">
                      <span className="text-[#64748B]">{k}</span>
                      <span className="text-[#1F2933] font-medium">{v || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Computed metrics */}
              {selected.computed && (
                <div>
                  <h3 className="text-sm font-bold text-[#1F2933] mb-2 uppercase tracking-wider">Computed Metrics</h3>
                  <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    {[
                      ['BMI', selected.computed.bmi, selected.computed.bmi_cat?.label],
                      ['LBM', selected.computed.lbm, null],
                      ['WHtR', selected.computed.whtr, selected.computed.whtr_cat?.label],
                      ['WHR', selected.computed.whr, selected.computed.whr_cat?.label],
                      ['eAG', selected.computed.eag, null],
                      ['HOMA-IR', selected.computed.homa_ir, selected.computed.homa_cat?.label],
                      ['MAP', selected.computed.map, selected.computed.map_cat?.label],
                      ['Pulse Pressure', selected.computed.pulse_pressure, selected.computed.pp_cat?.label],
                      ['LDL/HDL', selected.computed.ldl_hdl, selected.computed.ldl_hdl_cat?.label],
                      ['TG/HDL', selected.computed.tg_hdl, selected.computed.tg_hdl_cat?.label]
                    ].map(([k, v, badge]) => (
                      <div key={k} className="flex justify-between border-b border-[#E2E8F0] py-1">
                        <span className="text-[#64748B]">{k}</span>
                        <span className="text-[#1F2933] font-medium">
                          {v == null ? '-' : v}{badge && <span className="text-[#64748B] text-xs ml-1">· {badge}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Doctor notes */}
              <div>
                <h3 className="text-sm font-bold text-[#1F2933] mb-2 uppercase tracking-wider">Doctor Notes</h3>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={5}
                  placeholder="Consultation notes, follow-up items, recommendations…"
                  className="w-full bg-white border border-[#CBD5E1] rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none"
                  data-testid="ha-doctor-notes"
                ></textarea>
                <button
                  onClick={saveNotes}
                  disabled={savingNotes}
                  className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-[#1E88E5] hover:bg-[#1976D2] disabled:opacity-60 text-white text-sm font-semibold rounded-lg"
                  data-testid="ha-save-notes"
                >
                  <Save className="w-4 h-4" />
                  {savingNotes ? 'Saving…' : 'Save Notes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHealthAnalysesPage;
